'use client';

import { useEffect } from 'react';
import { useStore } from 'zustand';

import { PERMISSION } from '@/domain/permissions/constants/permissionIds';
import { usePermissions } from '@/domain/permissions/hooks/usePermissions';
import { usePortalAcesso } from '@/domain/portal/hooks/usePortalAcesso';
import { agenteStore } from '@/infrastructure/store/agenteStore';
import { authStore } from '@/infrastructure/store/authStore';

/**
 * Loads the agent conversation and provider health ONCE per session and account, from the
 * layout. The screens and the side panel only read the store: mounting them never refetches,
 * and nothing leaves before the unified login has a token (the layout mounts outside AuthGuard).
 * Switching accounts loads again: the conversation belongs to the account it was held in. A brand
 * buyer has no agent (the API refuses them everything outside the Portal), and neither has a role
 * without the Início (the API serves the agent under that id), so nothing loads for them.
 */
export function useAgenteBootstrap(): void {
  const isAuthenticated = useStore(authStore, (s) => s.isAuthenticated);
  const partnerId = useStore(authStore, (s) => s.user?.partnerId ?? '');
  const carregarConversa = useStore(agenteStore, (s) => s.carregarConversa);
  const carregarSaude = useStore(agenteStore, (s) => s.carregarSaude);
  const { isStaff } = usePortalAcesso();
  const { can } = usePermissions();
  const podeVerAgente = isStaff && can(PERMISSION.INICIO_VIEW);

  useEffect(() => {
    if (!isAuthenticated || partnerId === '' || !podeVerAgente) {
      return;
    }
    const run = async (): Promise<void> => {
      await Promise.all([carregarConversa(), carregarSaude()]);
    };
    run();
  }, [isAuthenticated, partnerId, podeVerAgente, carregarConversa, carregarSaude]);
}
